// Small cloud icon next to the page title that reflects network
// reachability:
//   green  – online and fast
//   yellow – online but slow (>1.5s ping)
//   red    – offline (browser flag or ping failed/timed out)
//   grey   – force-offline mode (user toggled manually)
//
// Click toggles force-offline mode so the user can lock
// the app into offline mode before heading into the rehearsal room.

import { useState } from 'react'
import { useOnlineStatus } from '../../lib/offline/useOnlineStatus'
import { isForceOffline, setForceOffline } from '../../config/offline'

const STATUS_COLOR: Record<ReturnType<typeof useOnlineStatus>, string> = {
  online: 'text-success',
  slow: 'text-yellow-400',
  offline: 'text-error',
}

export function OnlineIndicator() {
  const networkStatus = useOnlineStatus()
  const [forced, setForced] = useState(isForceOffline)

  const effectiveStatus = forced ? 'offline' : networkStatus
  const color = forced ? 'text-text-muted' : STATUS_COLOR[effectiveStatus]

  const label = forced
    ? 'Offline-Modus (manuell)'
    : networkStatus === 'online'
      ? 'Online'
      : networkStatus === 'slow'
        ? 'Online (langsam)'
        : 'Offline'

  function toggle() {
    const next = !forced
    setForceOffline(next)
    setForced(next)
  }

  return (
    <button
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 ${color}`}
      title={label}
      aria-label={label}
      onClick={toggle}
    >
      {effectiveStatus === 'offline' ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m2 2 20 20" />
          <path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193" />
          <path d="M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
      )}
      {forced && (
        <span className="text-[9px] font-bold uppercase tracking-wide">off</span>
      )}
    </button>
  )
}
